/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

// WEB half of the settings persistence layer (the native twin is
// `settingsStorage.native.ts` — bundler platform resolution picks the file, so
// keep the exported API of both in lockstep).
//
// This replaces the settings query's former reliance on the TanStack Query
// offline cache (`persistQueryClient`), which serialized the ENTIRE query
// cache through localStorage on the main thread. Settings only ever needed its
// own small state (values + etag) to survive reloads, so the web side now owns
// that in IndexedDB: async, off the main thread, and not bound by the ~5MB
// localStorage quota the offline cache competed for.
//
// Storage here is a cache, never a source of truth: every operation swallows
// failures (SSR, private browsing, blocked storage, quota) and resolves to
// undefined/no-op — the query then simply fetches without an etag.

import type { SettingsState } from '../hooks/settings';
import { Platform } from 'react-native';

/**
 * What actually goes into storage: the state plus WHEN it was fetched, so a
 * restart can seed the query cache with the snapshot's real age and TanStack's
 * staleness/refetch rules keep working (a day-old snapshot shows instantly but
 * still revalidates; a seconds-old one does not refetch pointlessly).
 */
export interface StoredSettings {
  state: SettingsState;
  storedAt: number;
}

/** Bump to discard previously stored blobs on shape changes. */
const STORAGE_VERSION = 'v1';

const IDB_NAME = 'move-travel-settings';
const IDB_STORE = 'settings';

/**
 * Storage key for one settings scope — deliberately NOT locale-scoped: one
 * blob per login state. After a locale switch the seed briefly shows the
 * previous locale's values, then the background revalidation replaces them
 * (the stored etag belongs to the other variant, so the server answers 200
 * with fresh data rather than 304). Must match the native twin's format.
 */
export function settingsStorageKey(loggedIn: boolean): string {
  return `move_travel_settings_${STORAGE_VERSION}:${loggedIn ? 'in' : 'out'}`;
}

/** SSR and blocked-storage guard — IndexedDB only exists in a real browser. */
const hasIndexedDb = (): boolean =>
  typeof window !== 'undefined' &&
  typeof indexedDB !== 'undefined' &&
  Platform.OS === 'web';

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(IDB_NAME, 1);
      request.onupgradeneeded = () => {
        request.result.createObjectStore(IDB_STORE);
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
      request.onblocked = () => reject(new Error('IndexedDB open blocked'));
    });
    // A failed open (e.g. Safari private mode) must not poison every later
    // attempt for the lifetime of the tab.
    dbPromise.catch(() => {
      dbPromise = null;
    });
  }
  return dbPromise;
}

function idbRequest<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(IDB_STORE, mode);
        const request = run(tx.objectStore(IDB_STORE));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      }),
  );
}

/** Load the persisted settings state for a scope; undefined when absent/broken. */
export async function loadStoredSettings(
  storageKey: string,
): Promise<StoredSettings | undefined> {
  if (!hasIndexedDb()) return undefined;
  try {
    const value = await idbRequest<unknown>('readonly', (store) =>
      store.get(storageKey),
    );
    return isStoredSettings(value) ? value : undefined;
  } catch {
    // Unreadable storage — treat as a cold cache.
    return undefined;
  }
}

/** Persist the settings state for a scope. Fire-and-forget safe. */
export async function saveStoredSettings(
  storageKey: string,
  state: SettingsState,
): Promise<void> {
  if (!hasIndexedDb()) return;
  try {
    const entry: StoredSettings = { state, storedAt: Date.now() };
    await idbRequest('readwrite', (store) => store.put(entry, storageKey));
  } catch {
    // Best-effort cache write; the next load just fetches without an etag.
  }
}

/** Drop the persisted settings state for a scope. */
export async function removeStoredSettings(storageKey: string): Promise<void> {
  if (!hasIndexedDb()) return;
  try {
    await idbRequest('readwrite', (store) => store.delete(storageKey));
  } catch {
    // Nothing to do — absent is the desired end state anyway.
  }
}

/** Shape guard so a corrupt/stale blob can never crash consumers. */
function isStoredSettings(value: unknown): value is StoredSettings {
  if (typeof value !== 'object' || value === null) return false;
  const entry = value as StoredSettings;
  return (
    typeof entry.storedAt === 'number' &&
    typeof entry.state === 'object' &&
    entry.state !== null &&
    // SettingsState types `version` as string, but the API returns a number at
    // runtime (observed: 2517) — a string-only check would silently reject
    // every stored entry and disable the cache-first seed.
    ['string', 'number'].includes(typeof entry.state.version) &&
    typeof entry.state.settings === 'object' &&
    entry.state.settings !== null
  );
}
